// src/components/ChessBasics.js
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import Chessground from "@react-chess/chessground";
import { Chess, SQUARES } from "chess.js";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  ListGroup,
} from "react-bootstrap";
import "../css/board.css";
// If your board renders without styles, uncomment ONE of these depending on your version:
// import "@react-chess/chessground/styles/chessground.css";
// import "chessground/dist/chessground.css";

/**
 * Interactive basics lessons + Stockfish reply (worker served from /public)
 * Primary:   /public/stockfish/src/stockfish-nnue-16-single.js  (+ .wasm)
 * Fallback:  /public/stockfish/src/stockfish-nnue-16-no-Worker.js (+ .wasm)
 */
const ENGINE_PRIMARY = "/stockfish/src/stockfish-nnue-16-single.js";
const ENGINE_FALLBACK = "/stockfish/src/stockfish-nnue-16-no-Worker.js";

function spawnEngine(url) {
  return new Worker(url);
}

const LESSONS = [
  {
    id: "pawn",
    title: "Pawn — how it moves",
    fen: "2k5/p1p2p2/P2P4/4P3/8/8/7P/7K w - - 0 1",
    color: "w",
    text: [
      "Pawns move forward one square (two from their starting rank).",
      "They capture one square diagonally forward.",
      "Pawns never move backwards.",
      "If a pawn reaches the last rank, it must promote to a piece (usually a queen).",
      "Pawn can not move forward if there is a piece blocking its path.",
    ],
  },
  {
    id: "knight",
    title: "Knight — L-shape",
    fen: "k7/8/8/8/4q1p1/4PpP1/5N2/3K4 w - - 0 1",
    color: "w",
    text: [
      "Knights move in an L-shape: two then one.",
      "They can jump over pieces.",
      "They cannot move to a square already occupied by a piece of the same color.",
      "Knights can attack pieces on squares they can move to.",
      "They can also attack pieces on squares they can jump to.",
    ],
  },
  {
    id: "bishop",
    title: "Bishop — diagonals",
    fen: "k7/8/1K6/1PB5/1p1P2B1/7q/8/8 w - - 0 1",
    color: "w",
    text: [
      "Bishops move any number of squares diagonally.",
      "They cannot jump over pieces.",
      "Each bishop stays on its original color throughout the game.",
      "They can attack pieces on squares they can move to.",
      "They cannot attack pieces on squares of the opposite color.",
    ],
  },
  {
    id: "rook",
    title: "Rook — ranks & files",
    fen: "k7/3q2Rp/1K6/8/6P1/8/8/8 w - - 0 1",
    color: "w",
    text: [
      "Rooks move any number of squares horizontally or vertically.",
      "They cannot jump over pieces.",
      "They can attack pieces on squares they can move to.",
      "They cannot attack pieces on squares diagonally.",
      "Rooks are powerful pieces, especially in the endgame.",
      "They can control open files and ranks.",
    ],
  },
  {
    id: "queen",
    title: "Queen",
    fen: "2k5/4QbN1/K2q1p2/1P6/4P3/8/7N/8 w - - 0 1",
    color: "w",
    text: [
      "The queen combines rook and bishop movement.",
      "Any number of squares along ranks, files, or diagonals.",
      "She cannot jump over pieces.",
      "The queen is the most powerful piece on the board.",
      "She can control both long-range and short-range squares.",
    ],
  },
  {
    id: "king",
    title: "King — one square & checks",
    fen: "8/8/2k5/8/1qKp4/3P4/2P5/8 w - - 0 1",
    color: "w",
    text: [
      "The king moves one square in any direction.",
      "You may not move into check.",
      "The king is the most important piece; if he is checkmated, you lose.",
      "If the king is in check, you must make a move to get out of check.",
      "King cannot move into check.",
      "If the king is attacked, you must move it or block the attack or capture the attacking piece.",
    ],
  },
  {
    id: "castling",
    title: "Castling — special move",
    fen: "4k3/8/8/8/8/8/8/R3K2R w KQ - 0 1",
    color: "w",
    text: [
      "King moves two squares towards a rook; the rook jumps over.",
      "Requirements: no pieces between; neither moved before",
      "king not in check; the squares the king crosses/lands on are not attacked.",
      "Castling is the only move that moves two pieces at once. Is special.",
    ],
  },
  {
    id: "enpassant",
    title: "En passant — special capture",
    fen: "8/1p1p1k2/3pp3/1PPPPPp1/8/8/8/4K3 w - g6 0 1",
    color: "w",
    text: [
      "If a pawn moves two squares and lands next to your pawn, you may capture it as if it moved one.",
      "Must be done immediately on the next move.",
      "If you don't capture en passant immediately, you lose the chance.",
      "which one you can capture en passant?",
    ],
  },
  {
    id: "promotion",
    title: "Promotion — last rank",
    fen: "k7/4P3/8/8/8/8/8/K7 w - - 0 1",
    color: "w",
    text: [
      "A pawn reaching the last rank must promote (usually to a queen).",
      "You can choose to promote to a queen, rook, bishop, or knight.",
      "Promotion is the only move that allows you to change a piece type.",
      "Most players always promote to a queen unless there's a specific reason.",
    ],
  },
  {
    id: "matef7",
    title: "Checkmate in 1 — f7",
    fen: "rnbqk2r/pppp1ppp/5n2/2b1p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR w KQkq - 0 1",
    color: "w",
    text: [
      "White to move and deliver checkmate in 1.",
      "The f7 pawn is a key piece in the checkmating pattern.",
    ],
  },
  {
    id: "free",
    title: "Free Play — starting position",
    fen: "start",
    color: "w",
    text: ["Play freely to reinforce the basics."],
  },
];

function getDests(chess) {
  const dests = new Map();
  SQUARES.forEach((s) => {
    const moves = chess.moves({ square: s, verbose: true });
    if (moves.length)
      dests.set(
        s,
        moves.map((m) => m.to)
      ); // arrays for Chessground
  });
  return dests;
}

function statusText(chess) {
  if (chess.isCheckmate())
    return `Checkmate — ${chess.turn() === "w" ? "Black" : "White"} wins`;
  if (chess.isStalemate()) return "Stalemate — draw";
  if (chess.isDraw()) return "Draw";
  const side = chess.turn() === "w" ? "White" : "Black";
  const check = chess.isCheck() ? " (in check)" : "";
  return `${side} to move${check}`;
}

export default function ChessBasics() {
  // Lesson
  const [lessonId, setLessonId] = useState(LESSONS[0].id);
  const lesson = useMemo(
    () => LESSONS.find((l) => l.id === lessonId) ?? LESSONS[0],
    [lessonId]
  );

  // Chess state
  const [chess, setChess] = useState(() => {
    const c = new Chess();
    if (lesson.fen !== "start") c.load(lesson.fen);
    return c;
  });

  // Board controls
  const [orientation, setOrientation] = useState("white");
  const [showMoves, setShowMoves] = useState(true);

  // Engine state
  const engineRef = useRef(null);
  const [engineOn, setEngineOn] = useState(true);
  const [engineReady, setEngineReady] = useState(false);
  const [engineThinking, setEngineThinking] = useState(false);
  const [engineName, setEngineName] = useState("");
  const [skill, setSkill] = useState(5); // 0–20
  const [moveTime, setMoveTime] = useState(800); // ms

  // Chessground config
  const [cgConfig, setCgConfig] = useState(() => ({
    fen: lesson.fen === "start" ? undefined : chess.fen(),
    orientation: "white",
    lastMove: [],
    highlight: { lastMove: true, check: true },
    movable: {
      color: "both",
      free: false,
      dests: getDests(chess),
    },
    events: {},
  }));

  // Reset on lesson change
  useEffect(() => {
    const c = new Chess();
    if (lesson.fen !== "start") c.load(lesson.fen);
    setChess(c);
    setOrientation(lesson.color === "b" ? "black" : "white");
    setEngineThinking(false);
  }, [lesson]);

  // --- Init Stockfish with fallback ---
  useEffect(() => {
    let engine = spawnEngine(ENGINE_PRIMARY);
    let ready = false;
    let fellBack = false;

    function wire(eng) {
      eng.onmessage = (e) => {
        const line = typeof e === "string" ? e : e.data;
        if (!line) return;
        // console.debug("[SF]", line);

        if (line.startsWith("id name")) {
          setEngineName(line.replace("id name", "").trim());
        }
        if (line === "uciok") {
          eng.postMessage("isready");
        }
        if (line === "readyok") {
          ready = true;
          setEngineReady(true);
          eng.postMessage(`setoption name Skill Level value ${skill}`);
          eng.postMessage("ucinewgame");
        }
        if (line.startsWith("bestmove")) {
          setEngineThinking(false);
          const uci = line.split(" ")[1];
          if (!uci || uci === "(none)") return;
          const from = uci.slice(0, 2);
          const to = uci.slice(2, 4);
          const promo = uci.slice(4, 5);
          setChess((prev) => {
            const c = new Chess(prev.fen());
            const mv = { from, to };
            if (promo) mv.promotion = promo.toLowerCase();
            c.move(mv);
            return c;
          });
        }
      };

      const onErr = () => {
        if (!fellBack) {
          try {
            eng.terminate();
          } catch {}
          engine = spawnEngine(ENGINE_FALLBACK);
          fellBack = true;
          wire(engine);
        }
      };
      eng.onerror = onErr;
      eng.onmessageerror = onErr;
      eng.postMessage("uci");
    }

    wire(engine);

    const t = setTimeout(() => {
      if (!ready && !fellBack) {
        try {
          engine.terminate();
        } catch {}
        engine = spawnEngine(ENGINE_FALLBACK);
        fellBack = true;
        wire(engine);
      }
    }, 2000);

    engineRef.current = engine;

    return () => {
      clearTimeout(t);
      try {
        engine.terminate();
      } catch {}
      engineRef.current = null;
      setEngineReady(false);
    };
  }, [skill]);

  // Update skill when ready/changed
  useEffect(() => {
    if (engineRef.current && engineReady) {
      engineRef.current.postMessage(
        `setoption name Skill Level value ${skill}`
      );
    }
  }, [skill, engineReady]);

  // Handle user moves (auto-queen)
  const onMove = useCallback(
    (orig, dest) => {
      if (engineThinking) return;

      const verbose = chess.moves({ verbose: true });
      const needsPromotion = verbose.some(
        (m) => m.from === orig && m.to === dest && m.promotion
      );

      const moved = needsPromotion
        ? chess.move({ from: orig, to: dest, promotion: "q" })
        : chess.move({ from: orig, to: dest });

      if (!moved) return;

      const nextFen = chess.fen();

      setCgConfig((prev) => ({
        ...prev,
        fen: nextFen,
        lastMove: [orig, dest],
        movable: {
          ...prev.movable,
          dests: showMoves ? getDests(chess) : new Map(),
        },
      }));
      setChess(new Chess(nextFen));

      const isOver = new Chess(nextFen).isGameOver();
      if (engineOn && engineReady && engineRef.current && !isOver) {
        setEngineThinking(true);
        engineRef.current.postMessage(`position fen ${nextFen}`);
        engineRef.current.postMessage(`go movetime ${moveTime}`);
      }
    },
    [chess, showMoves, engineOn, engineReady, engineThinking, moveTime]
  );

  // Keep board in sync
  useEffect(() => {
    setCgConfig((prev) => ({
      ...prev,
      orientation,
      fen: chess.fen(),
      movable: {
        ...prev.movable,
        color: "both",
        dests: engineThinking
          ? new Map()
          : showMoves
          ? getDests(chess)
          : new Map(),
      },
      check: chess.inCheck ? chess.inCheck() : false,
    }));
  }, [chess, orientation, showMoves, engineThinking]);

  // Attach handler to Chessground
  useEffect(() => {
    setCgConfig((prev) => ({
      ...prev,
      events: { ...(prev.events || {}), move: onMove },
    }));
  }, [onMove]);

  // Controls
  const handleReset = () => {
    const c = new Chess();
    if (lesson.fen !== "start") c.load(lesson.fen);
    setChess(c);
    setEngineThinking(false);
    setCgConfig((prev) => ({
      ...prev,
      fen: c.fen(),
      lastMove: [],
      movable: { ...prev.movable, dests: showMoves ? getDests(c) : new Map() },
    }));
    if (engineRef.current) engineRef.current.postMessage("ucinewgame");
  };

  const handleUndo = () => {
    const copy = new Chess(chess.fen());
    copy.undo();
    setChess(copy);
    setEngineThinking(false);
    setCgConfig((prev) => ({
      ...prev,
      fen: copy.fen(),
      lastMove: [],
      movable: {
        ...prev.movable,
        dests: showMoves ? getDests(copy) : new Map(),
      },
    }));
  };

  return (
    <Container fluid className="py-4 ch-rules-page">
      <Row className="mb-3">
        <Col>
          <h2 className="mb-1 ch-rules-title">Learn Chess: Interactive Basics</h2>
          <p className="mb-0 ch-rules-subtitle">
            Move a piece and the computer replies.{" "}
            {engineName && (
              <>
                Engine: <strong>{engineName}</strong>.
              </>
            )}
          </p>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Sidebar Lessons */}
        <Col xs={12} md={4} lg={3}>
          <Card className="shadow-sm ch-rules-card">
            <Card.Header className="fw-bold ch-rules-card-header">
              Lessons
            </Card.Header>
            <ListGroup variant="flush">
              {LESSONS.map((l) => (
                <ListGroup.Item
                  key={l.id}
                  action
                  className="ch-rules-list-item"
                  active={l.id === lessonId}
                  onClick={() => setLessonId(l.id)}
                >
                  {l.title}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
        </Col>

        {/* Main Board + Controls */}
        <Col xs={12} md={8} lg={9}>
          <Card className="shadow-sm mb-3 ch-rules-card">
            <Card.Body className="ch-rules-card-body">
              <Row className="align-items-center g-2 mb-3">
                <Col xs="auto">
                  <Button variant="outline-secondary" onClick={handleReset}>
                    Reset
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button variant="outline-secondary" onClick={handleUndo}>
                    Undo
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      setOrientation((o) => (o === "white" ? "black" : "white"))
                    }
                  >
                    Flip
                  </Button>
                </Col>
                <Col xs="auto">
                  <Form.Check
                    type="switch"
                    id="show-moves"
                    label="Show legal moves"
                    checked={showMoves}
                    onChange={(e) => setShowMoves(e.target.checked)}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Check
                    type="switch"
                    id="engine-on"
                    label="Computer reply"
                    checked={engineOn}
                    onChange={(e) => setEngineOn(e.target.checked)}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label className="mb-0 me-2">Skill</Form.Label>
                  <Form.Range
                    min={0}
                    max={80}
                    value={skill}
                    onChange={(e) => setSkill(Number(e.target.value))}
                    style={{ width: 120 }}
                  />
                </Col>
                <Col xs="auto">
                  <Form.Label className="mb-0 me-2">Think (ms)</Form.Label>
                  <Form.Control
                    type="number"
                    min={100}
                    step={100}
                    value={moveTime}
                    onChange={(e) => setMoveTime(Number(e.target.value || 800))}
                    style={{ width: 110 }}
                  />
                </Col>
              </Row>

              <div className="ch-rules-board-shell">
                <div className="ch-rules-board">
                  <Chessground config={cgConfig} contained />
                </div>
              </div>

              <div className="mt-3 small ch-rules-status">
                {engineThinking ? "Computer is thinking…" : statusText(chess)}
                {!engineReady && " (initialising engine…)"}
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm ch-rules-card">
            <Card.Header className="fw-bold ch-rules-card-header">
              {lesson.title}
            </Card.Header>
            <Card.Body className="ch-rules-card-body">
              {lesson.text.map((line, i) => (
                <p key={i} className="mb-2">
                  {line}
                </p>
              ))}
              {lesson.id === "castling" && (
                <ul className="mb-0">
                  <li>Kingside: king e1→g1 (white) / e8→g8 (black).</li>
                  <li>Queenside: king e1→c1 (white) / e8→c8 (black).</li>
                </ul>
              )}
              {lesson.id === "promotion" && (
                <p className="mb-0">
                  This demo auto-promotes to a queen for simplicity.
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
